import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import posthog from 'posthog-js';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import CookieConsent from './components/CookieConsent';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import PageSkeleton from './components/PageSkeleton';

// Lazy-loaded pages (code splitting)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const SignupSuccess = lazy(() => import('./pages/SignupSuccess'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const GiftCards = lazy(() => import('./pages/GiftCards'));
const GiftCardForm = lazy(() => import('./pages/GiftCardForm'));
const GiftCardDetail = lazy(() => import('./pages/GiftCardDetail'));
const Fornecedores = lazy(() => import('./pages/Fornecedores'));
const Transacoes = lazy(() => import('./pages/Transacoes'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Usuarios = lazy(() => import('./pages/Usuarios'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Verify2FA = lazy(() => import('./pages/Verify2FA'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const NotFound = lazy(() => import('./pages/NotFound'));
const OwnerConsole = lazy(() => import('./pages/OwnerConsole'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const BlogList = lazy(() => import('./blog/BlogList'));
const BlogPost = lazy(() => import('./blog/BlogPost'));
const CompareSpreadsheets = lazy(() => import('./pages/seo/CompareSpreadsheets'));
const CompareManual = lazy(() => import('./pages/seo/CompareManual'));
const ForAmazonSellers = lazy(() => import('./pages/seo/ForAmazonSellers'));
const ForResellers = lazy(() => import('./pages/seo/ForResellers'));
// DISABLED: external integrations (security review pending)
// const ApiDocs = lazy(() => import('./pages/ApiDocs'));
// const Integrations = lazy(() => import('./pages/Integrations'));
const SharedReport = lazy(() => import('./pages/SharedReport'));

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview');
    window.gtag?.('config', 'G-Q5LCW41FKS', { page_path: location.pathname });
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <PageViewTracker />
        <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* Home */}
          <Route path="/" element={<LandingPage />} />

          {/* Public pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup/success" element={<SignupSuccess />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-2fa" element={<Verify2FA />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* SEO landing pages */}
          <Route path="/compare/cardlockr-vs-spreadsheets" element={<CompareSpreadsheets />} />
          <Route path="/compare/cardlockr-vs-manual-tracking" element={<CompareManual />} />
          <Route path="/for/amazon-sellers" element={<ForAmazonSellers />} />
          <Route path="/for/gift-card-resellers" element={<ForResellers />} />

          {/* API & Integrations — DISABLED (security review pending) */}

          {/* Legal pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />

          {/* Public shared reports */}
          <Route path="/share/:token" element={<SharedReport />} />

          {/* Owner console */}
          <Route path="/owner-console" element={
            <ProtectedRoute>
              <OwnerConsole />
            </ProtectedRoute>
          } />

          {/* Protected app */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="gift-cards" element={<GiftCards />} />
            <Route path="gift-cards/new" element={<GiftCardForm />} />
            <Route path="gift-cards/:id/edit" element={<GiftCardForm />} />
            <Route path="gift-cards/:id" element={<GiftCardDetail />} />
            <Route path="suppliers" element={<Fornecedores />} />
            <Route path="transactions" element={<Transacoes />} />
            <Route path="reports" element={<Relatorios />} />
            <Route path="admin" element={<AdminPanel />} />
            <Route path="users" element={<Usuarios />} />
            <Route path="settings" element={<Configuracoes />} />
            <Route path="help" element={<HelpCenter />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <ToastContainer position="top-right" autoClose={3000} aria-live="polite" />
        <CookieConsent />
      </BrowserRouter>
      </ErrorBoundary>
    </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
